/**
 * Colour utility functions for theming
 */

/**
 * Calculate relative luminance of a colour
 * Based on WCAG 2.0 formula: https://www.w3.org/TR/WCAG20/#relativeluminancedef
 */
const getRelativeLuminance = (r: number, g: number, b: number): number => {
  const [rs, gs, bs] = [r, g, b].map(c => {
    const sRGB = c / 255;
    return sRGB <= 0.03928 ? sRGB / 12.92 : Math.pow((sRGB + 0.055) / 1.055, 2.4);
  });
  return 0.2126 * rs + 0.7152 * gs + 0.0722 * bs;
};

/**
 * Parse hex colour to RGB values
 */
const hexToRgb = (hex: string): { r: number; g: number; b: number } | null => {
  const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex);
  return result ? {
    r: parseInt(result[1], 16),
    g: parseInt(result[2], 16),
    b: parseInt(result[3], 16)
  } : null;
};

/**
 * Determine if a colour is light or dark based on luminance
 * Returns true if the colour is light (needs dark text)
 */
export const isLightColour = (hexColour: string): boolean => {
  const rgb = hexToRgb(hexColour);
  if (!rgb) return false;
  
  const luminance = getRelativeLuminance(rgb.r, rgb.g, rgb.b);
  // Use WCAG recommended threshold of 0.179 for better contrast
  // Colours with luminance > 0.179 are considered light and need dark text
  return luminance > 0.179;
};

/**
 * Get the appropriate text colour (black or white) for a given background colour
 * Ensures WCAG AA contrast ratio compliance
 */
export const getTextColourForBackground = (backgroundHex: string): '#000000' | '#ffffff' => {
  return isLightColour(backgroundHex) ? '#000000' : '#ffffff';
};

/**
 * Calculate contrast ratio between two colours
 * Used for WCAG compliance checking
 */
export const getContrastRatio = (colour1Hex: string, colour2Hex: string): number => {
  const rgb1 = hexToRgb(colour1Hex);
  const rgb2 = hexToRgb(colour2Hex);
  
  if (!rgb1 || !rgb2) return 0;
  
  const lum1 = getRelativeLuminance(rgb1.r, rgb1.g, rgb1.b);
  const lum2 = getRelativeLuminance(rgb2.r, rgb2.g, rgb2.b);
  
  const lighter = Math.max(lum1, lum2);
  const darker = Math.min(lum1, lum2);
  
  return (lighter + 0.05) / (darker + 0.05);
};

/**
 * Check if colour combination meets WCAG AA standard (4.5:1 for normal text)
 */
export const meetsWCAGAA = (backgroundHex: string, textHex: string): boolean => {
  const ratio = getContrastRatio(backgroundHex, textHex);
  return ratio >= 4.5;
};

/**
 * Check if colour combination meets WCAG AAA standard (7:1 for normal text)
 */
export const meetsWCAGAAA = (backgroundHex: string, textHex: string): boolean => {
  const ratio = getContrastRatio(backgroundHex, textHex);
  return ratio >= 7;
};
